import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { LoginItem } from '../../components/Login';
import { SignUpItem } from '../../components/SignupButton';
import { LogoutItem } from '../../components/LogoutButton';
import SettingsButton from '../components/SettingsButton';
import UnifiedSearchBar from '../components/unified-search/UnifiedSearchBar';
import ResultsManager from '../components/search/ResultsManager';
import { SearchResultsProvider, useSearchResults } from '../contexts/SearchResultsContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLoggedOutUserSearchCounter } from '../hooks/useLoggedOutUserSearchCounter';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import type { ReadDatabase, DatabaseList } from '../../api/OasisBackendApi';

const NavigationControls: React.FC = () => {
  const { state, dispatch } = useSearchResults();
  const canGoBack = state.currentIndex > 0;
  const canGoForward = state.currentIndex < state.history.length - 1;

  // Debug information
  console.log('Search State Debug:', {
    state
  });

  return (
    <div className="flex items-center gap-1 bg-[var(--rhea-bg-primary)] border border-[var(--rhea-card-border)] rounded-t-lg px-2 py-1 shadow-lg">
      <div className="text-xs text-[var(--rhea-text-secondary)] mr-1">
        {state.currentIndex + 1}/{state.history.length}
      </div>
      <button
        onClick={() => {
          console.log('Going back from index:', state.currentIndex);
          dispatch({ type: 'GO_BACK' });
        }}
        disabled={!canGoBack}
        className={`p-1 rounded transition-colors duration-200 ${
          canGoBack
            ? 'text-[var(--rhea-text-primary)] hover:bg-[var(--rhea-card-hover)]'
            : 'text-[var(--rhea-text-secondary)] opacity-50 cursor-not-allowed'
        }`}
        aria-label="Go back"
      >
        <ChevronLeft size={16} />
      </button>
      <button
        onClick={() => {
          console.log('Going forward from index:', state.currentIndex);
          dispatch({ type: 'GO_FORWARD' });
        }}
        disabled={!canGoForward}
        className={`p-1 rounded transition-colors duration-200 ${
          canGoForward
            ? 'text-[var(--rhea-text-primary)] hover:bg-[var(--rhea-card-hover)]'
            : 'text-[var(--rhea-text-secondary)] opacity-50 cursor-not-allowed'
        }`}
        aria-label="Go forward"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

const SearchEngine: React.FC = () => {
  const navigate = useNavigate();
  const { conversationId: conversationIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementSearchCount, LimitReachedMessage, hasReachedLimit, showLimitReached, setShowLimitReached, isInitialized } = useLoggedOutUserSearchCounter(loggedIn);
  
  // Get all parameters from URL using useMemo to prevent unnecessary recalculations
  const urlParams = React.useMemo(() => {
    const query = searchParams.get('q') || '';
    const modelFromUrl = searchParams.get('model') || 'gpt-4.1-mini';
    const agentFromUrl = searchParams.get('agent') || 'search_engine';
    
    console.log('SearchEngine: Received URL parameters:', {
      query,
      modelFromUrl,
      agentFromUrl,
      conversationId: conversationIdParam,
      allParams: Object.fromEntries(searchParams.entries())
    });
    
    return { query, modelFromUrl, agentFromUrl };
  }, [searchParams, conversationIdParam]);
  
  const [currentModel, setCurrentModel] = useState(urlParams.modelFromUrl);
  const [currentAgent, setCurrentAgent] = useState(urlParams.agentFromUrl);
  const [agentParams, setAgentParams] = useState<{
    domain_id: string[];
    available_domains?: { id: string; name: string }[];
    deep_research?: boolean;
  }>({ domain_id: [] });
  const [hasClearedQuery, setHasClearedQuery] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const resultsManagerRef = useRef<{ handleSendMessage: (message: string) => void } | null>(null);
  const db = useOasisBackend();
  const [domains, setDomains] = useState<ReadDatabase[] | null>(null);

  // Fetch domains
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await db.endpoints.rhea.rheaApiRheaAccountDatabasesRetrieve();
        const databaseList = response.data as DatabaseList;
        setDomains(databaseList.databases as ReadDatabase[]);
      } catch (error) {
        console.error('Error fetching domains:', error);
        setDomains([]);
      }
    };
    fetchDomains();
  }, [db]);

  // Extract agent parameters from URL - initialize immediately
  useEffect(() => {
    const params: {
      domain_id: string[];
      available_domains?: { id: string; name: string }[];
      deep_research?: boolean;
    } = { domain_id: [] };
    
    // Handle domain_id parameter
    const domainId = searchParams.get('domain_id');
    if (domainId) {
      if (urlParams.agentFromUrl === 'multi_domain') {
        // For multi-domain, split the comma-separated list
        params.domain_id = domainId.split(',').filter(id => id.length > 0);
        // Also store the full list as available_domains
        params.available_domains = domainId.split(',').filter(id => id.length > 0).map((id: string) => ({ id, name: id }));
      } else {
        // For search_engine, use as array (single domain)
        params.domain_id = domainId.split(',').filter(id => id.length > 0);
      }
    }

    // Handle available_domains parameter (JSON-encoded)
    const availableDomains = searchParams.get('available_domains');
    if (availableDomains) {
      try {
        const decodedDomains = JSON.parse(availableDomains);
        if (Array.isArray(decodedDomains) && decodedDomains.length > 0) {
          params.available_domains = decodedDomains;
          // If we have available_domains but no domain_id, use the first one
          if (!params.domain_id || params.domain_id.length === 0) {
            params.domain_id = [decodedDomains[0].id];
          }
        }
      } catch (error) {
        // Fallback to comma-separated format for backward compatibility
        console.log('Falling back to comma-separated format for available_domains');
        const fallbackDomains = availableDomains.split(',').filter(id => id.length > 0).map((id: string) => ({ id, name: id }));
        if (fallbackDomains.length > 0) {
          params.available_domains = fallbackDomains;
          if (!params.domain_id || params.domain_id.length === 0) {
            params.domain_id = [fallbackDomains[0].id];
          }
        }
      }
    }

    // Handle deep_research parameter
    const deepResearch = searchParams.get('deep_research');
    if (deepResearch !== null) {
      params.deep_research = deepResearch === 'true';
    }

    // Only update if we have meaningful params or if this is the initial load
    if (Object.keys(params).length > 0 && (params.domain_id.length > 0 || params.available_domains || params.deep_research !== undefined)) {
      setAgentParams(params);
    }
  }, [searchParams, urlParams.agentFromUrl]);

  // Clear the query parameter after initial load (only once)
  useEffect(() => {
    if (urlParams.query && !hasClearedQuery) {
      // Remove the query parameter immediately after the search is processed
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('q');
      setSearchParams(newParams);
      setHasClearedQuery(true);
    }
  }, [urlParams.query, hasClearedQuery, setSearchParams, searchParams]);

  const handleSearch = (searchQuery: string, params: { model: string; agent: string; agentParams: any }) => {

    // Check if user is logged in and has reached limit
    if (!loggedIn && hasReachedLimit) {
      setShowLimitReached(true);
      return;
    }

    // Increment search count for unauthenticated users
    if (!loggedIn) {
      incrementSearchCount();
    }
    
    // Mark that this is no longer the initial load
    setIsInitialLoad(false);
    
    // Call ResultsManager's handleSendMessage directly for follow-up searches
    if (resultsManagerRef.current) {
      resultsManagerRef.current.handleSendMessage(searchQuery);
    }
    
    // Update the current model and agent based on the search parameters
    setCurrentModel(params.model);
    setCurrentAgent(params.agent);
    setAgentParams(params.agentParams);
    
    // Update URL with all search parameters
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('q', searchQuery);
    newSearchParams.set('model', params.model);
    newSearchParams.set('agent', params.agent);
    
    // Add all agent parameters to URL
    Object.entries(params.agentParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'available_domains' && Array.isArray(value)) {
          // For available_domains, we need to preserve both id and name
          // We'll encode the full objects as JSON
          newSearchParams.set(key, JSON.stringify(value));
        } else if (key === 'deep_research') {
          // For deep_research, convert boolean to string
          newSearchParams.set(key, String(value));
        } else if (Array.isArray(value)) {
          // For other arrays (like domain_ids), join with commas
          newSearchParams.set(key, value.join(','));
        } else {
          newSearchParams.set(key, String(value));
        }
      }
    });

    setSearchParams(newSearchParams);
  };

  const handleNewSearch = () => {
    navigate('/');
  };

  return (
    <SearchResultsProvider>
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--rhea-bg-primary)', color: 'var(--rhea-text-primary)' }}>
        {/* Limit Reached Message */}
        <LimitReachedMessage />
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--rhea-card-border)]">
          <div className="flex items-center gap-2">
            <img 
              src="/assets/rhea/logo.png" 
              alt="Rhea" 
              className="h-8 cursor-pointer" 
              onClick={handleNewSearch}
            />
            <span 
              className="text-2xl font-semibold cursor-pointer" 
              style={{ color: 'var(--rhea-text-primary)' }}
              onClick={handleNewSearch}
            >
              Rhea
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {!loggedIn ? (
              <>
                <LoginItem title="Login to ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
                <SignUpItem title="Sign up for ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"/>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/chat/new')}
                  className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"
                >
                  Chat
                </button>
                <SettingsButton />
                <LogoutItem className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <ResultsManager
            initialMessage={isInitialLoad ? urlParams.query : undefined}
            currentAgent={currentAgent}
            currentModel={currentModel}
            agentParams={agentParams}
            conversationId={conversationIdParam ? parseInt(conversationIdParam) : null}
            onMount={(handleSendMessage) => {
              resultsManagerRef.current = { handleSendMessage };
            }}
          />
        </div>

        {/* Search Bar Dock with Navigation */}
        <div className="fixed bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-1 sm:px-4 pb-1 sm:pb-4">
            <div className="flex flex-col items-center justify-center">
              <NavigationControls />
                <UnifiedSearchBar
                  onSearch={handleSearch}
                  autoFocus={true}
                  initialModel={currentModel}
                  initialAgent={currentAgent}
                  initialAgentParams={agentParams}
                  onModelChange={setCurrentModel}
                  onAgentChange={setCurrentAgent}
                  onAgentParamsChange={setAgentParams}
                  conversationId={conversationIdParam ? parseInt(conversationIdParam) : undefined}
                  domains={domains}
                />
            </div>
          </div>
        </div>
      </div>
    </SearchResultsProvider>
  );
};

export default SearchEngine; 
